/**
 * Hook for managing product cache in IndexedDB
 * Provides offline access to products for barcode scanning
 */

import { useState, useCallback } from 'react';
import {
  openDatabase,
  STORES,
  CachedProduct,
  putMany,
  getByIndex,
  clearProductsForStore,
  count,
} from '../utils/indexedDB';
import { apiFetch, productsApi } from '../services/api';
import { codeNbrMatches } from '../utils/codeNbr';

interface ProductFromAPI {
  productid: number;
  upc: string;
  description: string;
  codenbr: string;
  cost: number | string;
  packsize: string;
  size: string;
  uom: string;
  brand: string;
  order_increment?: number;
}

// Backend returns: { status, message, data: { ship_to_address, bill_to_address, extra, data: [...products] } }
interface ProductsAPIResponseData {
  data: ProductFromAPI[];
  ship_to_address?: {
    firstname: string;
    lastname: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
  };
  bill_to_address?: {
    retailername: string;
    storeaddress1: string;
    storecity: string;
    storestate: string;
    storezip: string;
  };
  extra?: {
    showqtyonhand: boolean;
    showshelftags: boolean;
    minorderamount: number | null;
    maxproductcount: number | null;
  };
}

interface ProductsAPIResponse {
  status: string;
  message: string;
  data: ProductsAPIResponseData;
}

interface UseProductCacheResult {
  isLoading: boolean;
  error: string | null;
  cachedCount: number;
  cacheProductsForStore: (storeId: number, projectId: number) => Promise<number>;
  lookupByUPC: (upc: string, storeId: number) => Promise<CachedProduct | null>;
  lookupByCodeNbr: (codenbr: string, storeId: number) => Promise<CachedProduct | null>;
  getProductsForStore: (storeId: number) => Promise<CachedProduct[]>;
  clearCache: (storeId?: number) => Promise<void>;
  getCacheStats: () => Promise<{ totalProducts: number; lastSync?: string }>;
}

export const useProductCache = (): UseProductCacheResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedCount, setCachedCount] = useState(0);

  /**
   * Fetch products from API and cache them for offline use
   */
  const cacheProductsForStore = useCallback(
    async (storeId: number, projectId: number): Promise<number> => {
      setIsLoading(true);
      setError(null);

      try {
        // Get associate ID from localStorage
        // Try 'associate' first (new format), then fall back to 'rdm_user' (legacy)
        const userStr = localStorage.getItem('associate') || localStorage.getItem('rdm_user');
        if (!userStr) {
          throw new Error('User not logged in');
        }
        const user = JSON.parse(userStr);
        const associateId = user.associateid;

        if (!associateId) {
          throw new Error('Associate ID not found');
        }

        // Fetch products from API. Trailing slash is LOAD-BEARING (#2123):
        // without it Django APPEND_SLASH 301-redirects and fetch strips the
        // Authorization header on the cross-origin redirect -> guaranteed 401
        // ("Authentication credentials were not provided") no matter how
        // fresh the token is -> the product cache never populated on device.
        const response = await apiFetch<ProductsAPIResponse>(
          `/api/distributors/products/by-project/${projectId}/?storeid=${storeId}&associateid=${associateId}`
        );

        // Extract products array from nested response: data.data.data
        const productsArray = response.data?.data;
        if (!productsArray || !Array.isArray(productsArray)) {
          throw new Error('Invalid API response: no products array');
        }

        // Transform and cache products
        const cachedProducts: CachedProduct[] = productsArray.map((product) => ({
          productid: product.productid,
          upc: product.upc || '',
          description: product.description || '',
          codenbr: product.codenbr || '',
          cost: typeof product.cost === 'string' ? parseFloat(product.cost) : product.cost,
          packsize: product.packsize || '',
          size: product.size || '',
          uom: product.uom || '',
          brand: product.brand || '',
          order_increment: product.order_increment ?? 1,
          projectId,
          storeId,
          cachedAt: new Date().toISOString(),
        }));

        // Clear old products for this store and add new ones
        await clearProductsForStore(storeId);
        await putMany(STORES.PRODUCTS, cachedProducts);

        setCachedCount(cachedProducts.length);
        return cachedProducts.length;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to cache products';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Look up a product by UPC code (for barcode scanning)
   * First checks local cache, then falls back to API lookup across all distributors
   */
  const lookupByUPC = useCallback(
    async (upc: string, storeId: number): Promise<CachedProduct | null> => {
      try {
        await openDatabase();

        // Strip non-digit characters (dashes, spaces) from scanned UPC
        const cleanUpc = upc.replace(/[^0-9]/g, '');
        if (cleanUpc !== upc) {
          // If UPC had non-digits, also try the cleaned version
          const cleanProducts = await getByIndex<CachedProduct>(
            STORES.PRODUCTS,
            'upc_storeId',
            [cleanUpc, storeId]
          );
          if (cleanProducts.length > 0) {
            return cleanProducts[0];
          }
        }

        // Try exact match in local cache first
        const products = await getByIndex<CachedProduct>(
          STORES.PRODUCTS,
          'upc_storeId',
          [upc, storeId]
        );

        if (products.length > 0) {
          return products[0];
        }

        // Build a list of UPC variants to try against the local cache.
        // Scanners may return full 12-digit UPC-A (leading zero + 10-digit code + check digit)
        // while the database may store only the inner 10 digits.
        const variants: string[] = [];

        // Strip leading zeros
        const strippedUPC = upc.replace(/^0+/, '');
        variants.push(strippedUPC);

        // For 12-digit UPC-A: strip leading zero and check digit to get 10-digit code
        if (upc.length === 12) {
          variants.push(upc.slice(1, -1)); // e.g. 086800872740 -> 8680087274
        }

        // For 13-digit EAN-13: strip leading zero and check digit
        if (upc.length === 13) {
          variants.push(upc.slice(1, -1)); // inner 11 digits
          variants.push(upc.slice(0, -1)); // strip check digit only
        }

        // Strip check digit only (last digit) for any length
        if (upc.length >= 8) {
          variants.push(upc.slice(0, -1));
          variants.push(upc.replace(/^0+/, '').slice(0, -1));
        }

        // Pad short UPCs for UPC-A (12 digits) from UPC-E (8 digits)
        if (upc.length < 12) {
          variants.push(upc.padStart(12, '0'));
        }

        // Deduplicate and remove the original (already tried)
        const uniqueVariants = [...new Set(variants)].filter((v) => v !== upc && v.length > 0);

        for (const variant of uniqueVariants) {
          // Try compound index first (faster)
          const byStoreProducts = await getByIndex<CachedProduct>(
            STORES.PRODUCTS,
            'upc_storeId',
            [variant, storeId]
          );
          if (byStoreProducts.length > 0) {
            return byStoreProducts[0];
          }

          // Try UPC-only index and filter by store
          const byUpcProducts = await getByIndex<CachedProduct>(
            STORES.PRODUCTS,
            'upc',
            variant
          );
          const storeMatch = byUpcProducts.find((p) => p.storeId === storeId);
          if (storeMatch) {
            return storeMatch;
          }
        }

        // Not found in local cache - try API lookup across all distributors
        // This allows scanning products even when project doesn't have a distributor assigned
        try {
          const apiResults = await productsApi.lookupByUpc(upc);
          if (apiResults && apiResults.length > 0) {
            // Convert API result to CachedProduct format
            const firstResult = apiResults[0];
            const cachedProduct: CachedProduct = {
              productid: firstResult.productid,
              upc: firstResult.upc || upc,
              description: firstResult.description || '',
              codenbr: firstResult.codenbr || '',
              cost: firstResult.cost || 0,
              packsize: firstResult.packsize || '',
              size: firstResult.size || '',
              uom: firstResult.uom || '',
              brand: firstResult.brand || '',
              order_increment: (firstResult as { order_increment?: number }).order_increment ?? 1,
              projectId: 0, // Not project-specific
              storeId: storeId,
              distributorId: firstResult.distributor?.id || 0,
              cachedAt: new Date().toISOString(),
            };
            return cachedProduct;
          }
        } catch (apiErr) {
          console.error('API UPC lookup failed:', apiErr);
          // Fall through to return null
        }

        return null;
      } catch (err) {
        console.error('Error looking up product by UPC:', err);
        return null;
      }
    },
    []
  );

  /**
   * Look up a product by CodeNbr (the per-retailer code on a shelf tag) for the
   * #2022 OOS scan. The MSI/Plessey decoder (#1872) returns the shelf-tag digits
   * (assumed == CodeNbr); the cache is already scoped to the project's store, so
   * matching here is implicitly scoped to the project's retailer/distributor.
   * Tolerates leading zeros that a tag may carry vs. the stored code.
   */
  const lookupByCodeNbr = useCallback(
    async (codenbr: string, storeId: number): Promise<CachedProduct | null> => {
      try {
        await openDatabase();
        const products = await getByIndex<CachedProduct>(STORES.PRODUCTS, 'storeId', storeId);
        return products.find((p) => codeNbrMatches(p.codenbr, codenbr)) ?? null;
      } catch (err) {
        console.error('Error looking up product by CodeNbr:', err);
        return null;
      }
    },
    []
  );

  /**
   * Get all cached products for a store
   */
  const getProductsForStore = useCallback(async (storeId: number): Promise<CachedProduct[]> => {
    try {
      await openDatabase();
      const products = await getByIndex<CachedProduct>(STORES.PRODUCTS, 'storeId', storeId);
      return products;
    } catch (err) {
      console.error('Error getting products for store:', err);
      return [];
    }
  }, []);

  /**
   * Clear cached products (all or for specific store)
   */
  const clearCache = useCallback(async (storeId?: number): Promise<void> => {
    try {
      if (storeId) {
        await clearProductsForStore(storeId);
      } else {
        const { clear } = await import('../utils/indexedDB');
        await clear(STORES.PRODUCTS);
      }
      setCachedCount(0);
    } catch (err) {
      console.error('Error clearing product cache:', err);
    }
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(async (): Promise<{ totalProducts: number; lastSync?: string }> => {
    try {
      await openDatabase();
      const totalProducts = await count(STORES.PRODUCTS);
      return { totalProducts };
    } catch (err) {
      console.error('Error getting cache stats:', err);
      return { totalProducts: 0 };
    }
  }, []);

  return {
    isLoading,
    error,
    cachedCount,
    cacheProductsForStore,
    lookupByUPC,
    lookupByCodeNbr,
    getProductsForStore,
    clearCache,
    getCacheStats,
  };
};

export default useProductCache;
